import { useEffect, useState, useCallback } from "react";
import { Mail, RefreshCw, Eye, Send, Loader2, X, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";

const TEMPLATE_LABEL = {
  reminder: "Erinnerung (24h)", course_change: "Terminänderung", course_cancelled: "Kursabsage",
  booking_confirmation: "Buchungsbestätigung", invoice: "Rechnung/Bestätigung", payment_request: "Zahlungsaufforderung",
  payment_reminder: "Zahlungserinnerung", cancellation_confirmation: "Stornobestätigung",
  trackman_confirmation: "TrackMan-Bestätigung", custom: "Nachricht",
};

const StatusBadge = ({ delivery }) => {
  const map = {
    sent: ["Zugestellt", "text-emerald-400 border-emerald-400/40", CheckCircle2],
    failed: ["Fehlgeschlagen", "text-red-400 border-red-400/40", AlertTriangle],
    logged: ["Protokolliert", "text-neutral-400 border-neutral-500/40", FileText],
  };
  const [label, cls, Icon] = map[delivery] || [delivery, "text-neutral-400 border-neutral-500/40", FileText];
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-mono-lab uppercase tracking-wide ${cls}`}>
      <Icon size={11} /> {label}
    </span>
  );
};

const EmailLog = ({ canManage = true }) => {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const [template, setTemplate] = useState("");
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (template) params.set("template", template);
    if (q) params.set("q", q);
    api.get(`/admin/email-log?${params.toString()}`).then((r) => setItems(r.data.items)).catch((e) => toast.error(apiError(e)));
  }, [status, template, q]);

  useEffect(() => { load(); }, [load]);

  const openPreview = async (id) => {
    try {
      const r = await api.get(`/admin/email-log/${id}/preview`);
      setPreview(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const resend = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/email-log/${id}/resend`);
      toast.success(`Erneut gesendet (${r.data.delivery})`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/email/test-reminder`, { to: testEmail });
      toast.success(`Test-Erinnerung gesendet an ${r.data.to} (${r.data.delivery})`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-emaillog">
      <div className="flex items-center gap-3 mb-6">
        <Mail className="text-[#ff0033]" />
        <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white">E-Mail-Protokoll</h1>
      </div>

      {/* Filter + Test */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <input data-testid="emaillog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche (E-Mail, Kurs, Betreff, Nr.)…"
          className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#ff0033] outline-none min-w-[220px] flex-1" />
        <select data-testid="emaillog-status" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#ff0033] outline-none">
          <option value="">Alle Status</option>
          <option value="sent">Zugestellt</option>
          <option value="failed">Fehlgeschlagen</option>
          <option value="logged">Protokolliert</option>
        </select>
        <select data-testid="emaillog-template" value={template} onChange={(e) => setTemplate(e.target.value)} className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#ff0033] outline-none">
          <option value="">Alle Typen</option>
          {Object.entries(TEMPLATE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={load} data-testid="emaillog-refresh" className="flex items-center gap-2 border border-white/20 text-white px-4 py-2.5 text-sm hover:border-[#ff0033] transition-colors"><RefreshCw size={15} /> Aktualisieren</button>
      </div>

      {canManage && (
        <div className="flex flex-wrap items-center gap-3 mb-6 bg-[#0F0F0F] border border-white/10 p-4">
          <span className="font-mono-lab text-[11px] uppercase tracking-widest text-neutral-500">Test-Erinnerung senden an:</span>
          <input data-testid="emaillog-test-email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="admin@… (leer = eigenes Konto)"
            className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#ff0033] outline-none min-w-[240px]" />
          <button onClick={sendTest} disabled={busy} data-testid="emaillog-send-test" className="flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-4 py-2 text-sm hover:bg-[#cc0029] transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Test senden
          </button>
        </div>
      )}

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#ff0033]" /></div>
      ) : items.length === 0 ? (
        <p className="text-neutral-500">Keine E-Mails gefunden.</p>
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0F0F0F] text-neutral-400 font-mono-lab text-[10px] uppercase tracking-widest">
                <th className="text-left px-4 py-3">Zeitpunkt</th>
                <th className="text-left px-4 py-3">Typ</th>
                <th className="text-left px-4 py-3">Empfänger</th>
                <th className="text-left px-4 py-3">Betreff / Kurs</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} data-testid={`emaillog-row-${e.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                  <td className="px-4 py-3 text-neutral-300">{TEMPLATE_LABEL[e.template] || e.template}</td>
                  <td className="px-4 py-3 text-neutral-300">{e.to}</td>
                  <td className="px-4 py-3 text-neutral-400 max-w-[280px] truncate">{e.context?.course || e.subject}</td>
                  <td className="px-4 py-3"><StatusBadge delivery={e.delivery} />{e.error ? <span className="block text-[10px] text-red-400 mt-1 max-w-[200px] truncate" title={e.error}>{e.error}</span> : null}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openPreview(e.id)} data-testid={`emaillog-preview-${e.id}`} className="inline-flex items-center gap-1 text-neutral-300 hover:text-[#ff0033] mr-3"><Eye size={14} /> Vorschau</button>
                    {canManage && <button onClick={() => resend(e.id)} disabled={busy} data-testid={`emaillog-resend-${e.id}`} className="inline-flex items-center gap-1 text-neutral-300 hover:text-[#ff0033] disabled:opacity-50"><Send size={14} /> Erneut</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setPreview(null)}>
          <div className="bg-[#0F0F0F] border border-white/15 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()} data-testid="emaillog-preview-modal">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 sticky top-0 bg-[#0F0F0F]">
              <div className="flex items-center gap-2"><StatusBadge delivery={preview.delivery} /><span className="text-neutral-500 text-xs">{preview.to}</span></div>
              <button onClick={() => setPreview(null)} data-testid="emaillog-preview-close" className="text-neutral-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5">
              <p className="font-heading text-lg text-white mb-1">{preview.subject}</p>
              <p className="text-[11px] font-mono-lab uppercase tracking-widest text-neutral-500 mb-4">{TEMPLATE_LABEL[preview.template] || preview.template} · {fmtDateTime(preview.created_at)}</p>
              <pre className="whitespace-pre-wrap text-neutral-300 text-sm leading-relaxed font-sans">{preview.body}</pre>
              {preview.error ? <p className="text-red-400 text-xs mt-4">Fehler: {preview.error}</p> : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLog;
